"use client";

import { useState, useEffect } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import { Button } from "../ui/button";
import config from "../../config/config";
import { MessageSquare, CheckCircle, ArrowRight } from "lucide-react";

export default function IssueSection() {
  const [issueData, setIssueData] = useState(null);
  const [sectionTitle, setSectionTitle] = useState(null);
  const [sectionSubtitle, setSectionSubtitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchIssueSettings = async () => {
      try {
        setLoading(true);
        console.log('Fetching issue settings from:', `${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/issue`);
        
        const response = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/issue`);
        console.log('Issue API Response Status:', response.status);
        
        const data = await response.json();
        console.log('Issue API Response Data:', data);
        
        if (data.success && data.data) {
          setIssueData(data.data);
          console.log('Issue data set:', data.data);
        }
        if (data.success && data.data.sectionTitle) {
          setSectionTitle(data.data.sectionTitle);
        }
        if (data.success && data.data.sectionSubtitle) {
          setSectionSubtitle(data.data.sectionSubtitle);
        }
      } catch (error) {
        console.error('Error fetching issue settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIssueSettings();
  }, [language]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      if (field[language]) return field[language];
      if (field.en) return field.en;
    }
    return '';
  };

  if (loading) {
    return null;
  }
  
  console.log('Issue data state:', { issueData, sectionTitle, sectionSubtitle });
  
  if (!issueData) {
    console.log('No issue data found');
    return null;
  }

  // More flexible check for features
  const hasFeatures = issueData.features && issueData.features.length > 0;
  console.log('Has features:', hasFeatures, issueData.features);

  // Check if there's any meaningful content to display
  const hasContent = issueData.desktopImage || 
                     getLabel(sectionTitle) || 
                     getLabel(sectionSubtitle) ||
                     hasFeatures;

  console.log('Has content:', hasContent, {
    desktopImage: !!issueData.desktopImage,
    sectionTitle: !!getLabel(sectionTitle),
    sectionSubtitle: !!getLabel(sectionSubtitle),
    hasFeatures
  });

  if (!hasContent) {
    console.log('No meaningful content to display');
    return null;
  }

  return (
    <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6" style={{ backgroundColor: 'var(--color-muted)' }}>
      <div className="max-w-7xl mx-auto w-full">
        
        {/* Mobile Layout */}
        <div className="lg:hidden">
          {/* Full Width Image with Overlay */}
          {issueData.desktopImage && (
            <div className="relative w-full aspect-square rounded-md overflow-hidden mb-4">
              <img
                src={issueData.mobileImage ? issueData.mobileImage : issueData.desktopImage}
                alt={getLabel(sectionTitle)}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              
              {/* Overlay Badge and Title at Bottom Center */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent p-4 text-center">
                {getLabel(sectionTitle) && (
                  <div className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1 rounded-full mb-2" 
                       style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-primary-foreground)' }}>
                    <MessageSquare className="w-3 h-3" />
                    <span className="text-xs font-medium">
                      {getLabel(sectionTitle)}
                    </span>
                  </div>
                )}
                
                {getLabel(sectionSubtitle) && (
                  <h2 className="text-sm font-semibold leading-tight text-white text-center">
                    {getLabel(sectionSubtitle)}
                  </h2>
                )}
              </div>
            </div>
          )}

          {/* Features Section */}
          {hasFeatures && (
            <div className="space-y-3 mb-4">
              {issueData.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-primary-foreground)' }}>
                    <CheckCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold" style={{ color: 'var(--color-foreground)' }}>
                      {getLabel(feature.title)}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
                      {getLabel(feature.description)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Full Width Button */}
          {issueData.buttonText && issueData.buttonLink && (
            <Button
              onClick={() => {
                const target = issueData.buttonTarget ? issueData.buttonTarget : '_self';
                const link = issueData.buttonLink;
                if (target === "_blank") {
                  window.open(link, "_blank");
                } else {
                  window.location.href = link;
                }
              }}
              className="w-full px-4 py-2 text-sm font-medium"
              style={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-primary-foreground)'
              }}
            >
              {getLabel(issueData.buttonText)}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          )}
        </div>



        {/* Desktop Layout - Hidden on mobile */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-start">
            {/* Left Side - Information */}
            <div className="space-y-4">
              {/* Header */}
              <div className="space-y-3">
                {getLabel(sectionTitle) && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" 
                       style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-primary-foreground)' }}>
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {getLabel(sectionTitle)}
                    </span>
                  </div>
                )}
                
                {getLabel(sectionSubtitle) && (
                  <h2 className="text-lg md:text-xl font-semibold" style={{ color: 'var(--color-foreground)' }}>
                    {getLabel(sectionSubtitle)}
                  </h2>
                )}
              </div>

              {/* Features */}
              {hasFeatures && (
                <div className="space-y-3">
                  {issueData.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                    <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-primary-foreground)' }}>
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm md:text-base font-semibold" style={{ color: 'var(--color-foreground)' }}>
                        {getLabel(feature.title)}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                        {getLabel(feature.description)}
                      </p>
                    </div>
                  </div>
                  ))}
                </div>
              )}

              {/* Call to Action Button */}
              {issueData.buttonText && issueData.buttonLink && (
                <div className="mt-4">
                  <Button
                    onClick={() => {
                      const target = issueData.buttonTarget ? issueData.buttonTarget : '_self';
                      const link = issueData.buttonLink;
                      if (target === "_blank") {
                        window.open(link, "_blank");
                      } else {
                        window.location.href = link;
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-primary-foreground)'
                    }}
                  >
                    {getLabel(issueData.buttonText)}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Right Side - Image */}
            <div className="w-full rounded-md border overflow-hidden">
              {issueData.desktopImage && (
                <div className="relative w-full h-auto">
                  <img
                    src={issueData.desktopImage}
                    alt={getLabel(sectionTitle)}
                    className="w-full h-full object-cover"
                    style={{ maxHeight: '500px' }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}